/**
 * Global Filters property editor, shown in the titlebar component's properties. Edits the
 * design-level `globalFilters` list (up to the server-passed maxGlobalFilters definitions inline).
 * There is no Apply button; edits are written to the in-memory design and are only persisted
 * when the dashboard is saved.
 */
namespace distill.dashboards {
    const GLOBAL_FILTER_MIN_RESULTS_FOR_SEARCH: string = '10';

    export class GlobalFiltersEditor extends EditorBase {
        private $list!: JQuery;
        private $addBtn!: JQuery;
        private $limitHint!: JQuery;
        private _draft: IDashboardGlobalFilter[] = [];

        // Global filters live at design level (design.globalFilters), not on the titlebar
        // component the property pane is bound to, so the component-state accessors are
        // overridden to read/write the design instead.
        protected getValue<T>(): any {
            return Object.freeze(this.getFilters());
        }

        protected getValueForEdit<T>(): any {
            return appn.utils.deepClone(this.getFilters());
        }

        protected setValue(newValue: any): void {
            designer.updateGlobalFilters(newValue || []);
        }

        protected init(): void {
            this.$list = this.$editor.find('.global-filters-list');
            this.$addBtn = this.$editor.find('.add-global-filter');
            this.$limitHint = this.$editor.find('.global-filter-limit-hint');
        }

        public update(): void {
            this._draft = this.getValueForEdit();
            this.renderRows();
        }

        protected bindEvents(): void {
            this.$addBtn.on('click', (e) => {
                e.preventDefault();
                this.addFilter();
            });

            this.$editor.on('click', '.remove-global-filter', (e) => {
                e.preventDefault();
                this._draft.splice($(e.currentTarget).closest('.globalFilterRow').index(), 1);
                this.commit();
            });

            this.$editor.on('change input', '.global-filter-label', e => {
                const index = this.rowIndex(e);
                const isBlur = e.type === 'change';

                // Clearing the label reverts the row to its default label on blur.
                if (isBlur && !($(e.target).val() as string || '').trim()) {
                    const defaultLabel = this.defaultFilterLabel(index);
                    $(e.target).val(defaultLabel);
                    this.updateFilter(index, { label: defaultLabel });
                    return;
                }

                this.updateFilter(index, { label: ($(e.target).val() as string) || '' });
            });

            // Placeholder-style defaults: focusing a field that shows its default ("Filter N")
            // clears it so the user can type straight away; blurring blank restores the default
            // (handled in the change handler above).
            this.$editor.on('focus', '.global-filter-label', e => {
                const $input = $(e.target);
                if (($input.val() as string) === this.defaultFilterLabel(this.rowIndex(e)))
                    $input.val('');
            });

            // Data Name: the URL parameter base. Stored trimmed; blank = derive from the label.
            // The charset/uniqueness rules are enforced by the design save validation.
            this.$editor.on('change input', '.global-filter-data-name', e => {
                const value = ($(e.target).val() as string || '').trim();
                this.updateFilter(this.rowIndex(e), { dataName: value || null });
            });

            this.$editor.on('change', '.global-filter-type', e => {
                const type = $(e.target).val() as DashboardGlobalFilterType;
                const updates: Partial<IDashboardGlobalFilter> = { type };
                if (type !== DashboardGlobalFilterType.Dropdown) {
                    updates.sourceDatasetId = null;
                    updates.displayColumn = null;
                    updates.valueColumn = null;
                }
                this.updateFilter(this.rowIndex(e), updates);
            });

            this.$editor.on('change', '.global-filter-dataset', e => {
                this.updateFilter(this.rowIndex(e), {
                    sourceDatasetId: ($(e.target).val() as string) || null,
                    displayColumn: null,
                    valueColumn: null
                });
            });

            // Selecting a display column always overrides the value column with the same
            // column; a different value column can be chosen afterwards.
            this.$editor.on('change', '.global-filter-display-column', e => {
                const displayColumn = ($(e.target).val() as string) || null;
                this.updateFilter(this.rowIndex(e), { displayColumn, valueColumn: displayColumn });
            });

            this.$editor.on('change', '.global-filter-value-column', e => {
                this.updateFilter(this.rowIndex(e), { valueColumn: ($(e.target).val() as string) || null });
            });
        }

        private rowIndex(e: JQuery.TriggeredEvent): number {
            return $(e.currentTarget).closest('.globalFilterRow').index();
        }

        private renderRows(): void {
            if (!this.$list || !this.$list.length)
                return;

            // Sync the list incrementally: rows are keyed by filter id and only new or
            // mismatched rows are rebuilt. Tearing down the whole list inside a select2
            // change dispatch destroys the widget the event came from while its dropdown
            // is still closing — the leaked body-attached dropdown then blocks property
            // pane scrolling.
            const $existing = this.$list.children('.globalFilterRow');
            for (let i = $existing.length - 1; i >= this._draft.length; i--)
                $($existing[i]).remove();

            for (let i = 0; i < this._draft.length; i++) {
                const filter = this._draft[i];
                const $row = i < $existing.length ? $($existing[i]) : null;

                if ($row && $row.attr('data-filter-id') === filter.id)
                    this.syncRow($row, filter);
                else if ($row)
                    $row.replaceWith(this.buildRow(filter));
                else
                    this.$list.append(this.buildRow(filter));
            }

            const atLimit = this._draft.length >= designer.maxGlobalFilters;
            this.$addBtn.toggleClass('d-none', atLimit || !this._canSave);
            this.$limitHint.toggleClass('d-none', !atLimit);

            this.initSelects();

            if (!this._canSave)
                this.$editor.find('input, select, .remove-global-filter, .add-global-filter').prop('disabled', true);
        }

        // Controlled state → DOM sync for an existing row: updates values and visibility in
        // place (select2 displays via change.select2, which does not re-fire our handlers)
        // instead of rebuilding the row's widgets.
        private syncRow($row: JQuery, filter: IDashboardGlobalFilter): void {
            $row.find('.global-filter-label').val(filter.label || '');
            $row.find('.global-filter-data-name').val(filter.dataName || '');

            const type = filter.type || DashboardGlobalFilterType.Text;
            const $type = $row.find('.global-filter-type');
            if ($type.val() !== type) {
                $type.val(type);
                $type.trigger('change.select2');
            }

            $row.find('.global-filter-dropdown-config').toggleClass('d-none', filter.type !== DashboardGlobalFilterType.Dropdown);

            const datasetId = filter.sourceDatasetId || '';
            const $dataset = $row.find('.global-filter-dataset');
            if ($dataset.val() !== datasetId) {
                this.populateDatasets($dataset, datasetId);
                $dataset.trigger('change.select2');
            }

            const datasetIdForColumns = filter.sourceDatasetId || null;
            this.syncColumnSelect($row.find('.global-filter-display-column'), datasetIdForColumns, filter.displayColumn || '', T('Select display column...'));
            this.syncColumnSelect($row.find('.global-filter-value-column'), datasetIdForColumns, filter.valueColumn || '', T('Select value column...'));
        }

        private syncColumnSelect($select: JQuery, datasetId: string | null, selectedId: string, placeholderText: string): void {
            this.populateColumns($select, datasetId, selectedId, placeholderText);
            $select.trigger('change.select2');
        }

        private initSelects(): void {
            if (!this.$editor || !this.$editor.length || !this.$editor.is(':visible'))
                return;

            // Dataset selector mirrors the widget dataset selector (origin icons + hierarchy).
            appn.select.initForDesign({
                elements: this.$editor.find('.global-filter-dataset').not('.select2-hidden-accessible'),
                minimumResultsForSearch: GLOBAL_FILTER_MIN_RESULTS_FOR_SEARCH,
                isIconTextSelect: true,
                isHierarchical: true
            });

            appn.select.initForDesign({
                elements: this.$editor.find('.global-filter-type, .global-filter-display-column, .global-filter-value-column').not('.select2-hidden-accessible'),
                omitWidth: true,
                minimumResultsForSearch: GLOBAL_FILTER_MIN_RESULTS_FOR_SEARCH
            });
        }

        private addFilter(): void {
            if (!this._canSave || this._draft.length >= designer.maxGlobalFilters)
                return;

            // Pre-populate with the next free number: "Filter N". N is index + 1 in the normal
            // append case; skipping numbers other filters already use keeps labels unique
            // after removals.
            const number = this.nextFilterNumber();
            this._draft.push({
                id: appn.utils.newGuid(true),
                label: this.defaultFilterLabel(number - 1),
                type: DashboardGlobalFilterType.Text,
                sourceDatasetId: null,
                displayColumn: null,
                valueColumn: null
            });

            this.commit();
        }

        private nextFilterNumber(): number {
            let number = this._draft.length + 1;
            while (this._draft.some(filter => filter.label?.toLowerCase() === this.defaultFilterLabel(number - 1).toLowerCase()))
                number++;
            return number;
        }

        private defaultFilterLabel(index: number): string {
            return T('Filter {0}', (index + 1).toString());
        }

        private updateFilter(index: number, updates: Partial<IDashboardGlobalFilter>): void {
            if (!this._draft[index])
                return;

            this._draft[index] = { ...this._draft[index], ...updates };
            this.commit();
        }

        // State flows out through setValue → PROPERTY_UPDATED → update() → renderRows; the
        // editor never re-renders itself inline (editor state model contract).
        private commit(): void {
            this.setValue(this._draft);
        }

        private buildRow(filter: IDashboardGlobalFilter): JQuery {
            const $row = appn.utils.cloneTemplate('globalFilterRowTemplate');
            $row.attr('data-filter-id', filter.id);

            $row.find('.global-filter-label').val(filter.label || '');
            $row.find('.global-filter-data-name').val(filter.dataName || '');
            $row.find('.global-filter-type').val(filter.type || DashboardGlobalFilterType.Text);

            const $dropdownConfig = $row.find('.global-filter-dropdown-config');
            $dropdownConfig.toggleClass('d-none', filter.type !== DashboardGlobalFilterType.Dropdown);

            this.populateDatasets($row.find('.global-filter-dataset'), filter.sourceDatasetId || '');
            this.populateColumns($row.find('.global-filter-display-column'), filter.sourceDatasetId || null, filter.displayColumn || '',
                T('Select display column...') );
            this.populateColumns($row.find('.global-filter-value-column'), filter.sourceDatasetId || null, filter.valueColumn || '',
                T('Select value column...'));

            return $row;
        }

        private populateDatasets($select: JQuery, selectedId: string): void {
            $select.empty();
            $select.append($('<option>').val('').text(T('Choose dataset...')));

            (designer.getDatasets() || []).forEach(dataset => {
                const $option = $('<option>').val(dataset.id);
                buildDatasetOption($option, dataset);
                $select.append($option);
            });

            $select.val(selectedId || '');
        }

        private populateColumns($select: JQuery, datasetId: string | null, selectedId: string, placeholderText: string): void {
            $select.empty();
            $select.append($('<option>').val('').text(placeholderText));

            if (datasetId) {
                const dataset = (designer.getDatasets() || []).find(d => d.id === datasetId);
                if (dataset)
                    (dataset.columns || []).forEach(column => $select.append($('<option>').val(column.id).text(getColumnOptionText(column))));
            }

            $select.val(selectedId || '');
        }

        // -- Shared helpers --

        private getFilters(): IDashboardGlobalFilter[] {
            const design = designer._design as IDashboardDesign;
            return (design && design.globalFilters) ? design.globalFilters : [];
        }
    }

    editors.register('globalFiltersEditor', GlobalFiltersEditor);
}
